import { useEffect, useMemo } from 'react';
import { Route, Switch } from 'react-router';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { EnhancedError } from '../../components/_error/components/EnhancedError';
import { Redirect } from '../../components/redirect/Redirect';
import { useSearchParams } from '../../hooks/useSearchParams';
import { useUrl } from '../../utils/api';
import { isServer } from '../../utils/utils';
import { configHtmlCache } from '../utils/config-html-cache';
import { PageUrl } from '../utils/page-url';
import { AccountLayout } from './components/account-layout/AccountLayout';
import { ForgotPassword } from './components/forgot-password/ForgotPassword';
import { Login } from './components/login/Login';
import { Registration } from './components/registration/Registration';
import { ResetPassword } from './components/reset-password/ResetPassword';
import { AccountSkeletons } from './components/skeletons/AccountSkeletons';
import { TrackOrder } from './components/track-order/TrackOrder';
import { useSLTCustomerType } from './useSLTCustomerType';

const GUEST_ONLY_ROUTES = [
    PageUrl.ACCOUNT_LOGIN.toString(),
    PageUrl.ACCOUNT_REGISTRATION.toString(),
    PageUrl.TRACK_ORDER.toString(),
];

const Account: PageFC = ({ location, match }) => {
    const accountUrl = useUrl(PageUrl.ACCOUNT_SHOW);
    const {
        data: customerType,
        isLoading: isCustomerTypeLoading,
        refetch,
    } = useSLTCustomerType({
        staleTime: Infinity,
        enabled: !isServer,
    });
    const query = useSearchParams();
    const redirectUrl = useMemo(
        () => decodeURIComponent(query.get('redirect-url') ?? '') || accountUrl,
        [accountUrl, query],
    );

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refetch();
    }, [refetch, location]);

    const isGuestOnlyRoute = useMemo(
        () => GUEST_ONLY_ROUTES.includes(location?.pathname ?? ''),
        [location],
    );
    const shouldRedirect = (isGuestOnlyRoute && customerType === 'registered') || !!match?.isExact;

    if (isCustomerTypeLoading || shouldRedirect) {
        return (
            <>
                <AccountSkeletons />
                {shouldRedirect ? <Redirect to={redirectUrl} type="sg" /> : null}
            </>
        );
    }

    return (
        <div className="h-full w-full">
            <Switch>
                <Route exact path={PageUrl.ACCOUNT_LOGIN}>
                    <AccountLayout showCreateAccountLink>
                        <Login />
                    </AccountLayout>
                </Route>
                <Route exact path={PageUrl.ACCOUNT_REGISTRATION}>
                    <AccountLayout>
                        <Registration />
                    </AccountLayout>
                </Route>
                <Route exact path={PageUrl.ACCOUNT_FORGOT_PASSWORD}>
                    <ForgotPassword />
                </Route>
                <Route exact path={PageUrl.ACCOUNT_RESET_PASSWORD}>
                    <ResetPassword />
                </Route>
                <Route exact path={PageUrl.TRACK_ORDER}>
                    <AccountLayout showCreateAccountLink>
                        <TrackOrder />
                    </AccountLayout>
                </Route>
                <Route path="*">
                    <EnhancedError />
                </Route>
            </Switch>
        </div>
    );
};

Account.defaultProps = {};

Account.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

Account.getProps = ({ res }): Promise<object> => {
    configHtmlCache(res);
    return Promise.resolve({});
};

export default Account;
